import { useMemo, type ReactNode } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * Tabela de listas densas (Leads, Clientes, Lixeira, Empresas).
 *
 * Usa o TanStack Table apenas como modelo headless — sem estilo próprio da
 * biblioteca —, enquanto filtro, ordenação e paginação continuam no modelo puro
 * da feature. Assim a mesma seleção que alimenta o contador alimenta a tabela
 * (AGENTS §10).
 *
 * Acessibilidade: o cabeçalho ordenável é um botão que anuncia coluna e direção
 * por `aria-sort` (§17); a região de rolagem é focável e nomeada; a linha
 * inteira responde ao clique do mouse por conveniência, mas o caminho de
 * teclado é o botão do próprio nome, nunca um `tr` com `tabindex`.
 */
export interface DataTableColumn<Row> {
  id: string;
  label: string;
  /** Largura em px; alimenta o `colgroup` e a largura mínima da tabela. */
  width: number;
  numeric?: boolean;
  /** Presente quando a coluna ordena; identifica a chave no estado externo. */
  sortKey?: string;
  cell: (row: Row) => ReactNode;
  /** Conteúdo ao lado do rótulo, como a ajuda de um indicador. */
  headerExtra?: ReactNode;
}

export interface DataTableSort {
  key: string;
  direction: "asc" | "desc";
}

export interface DataTableProps<Row> {
  /** Nome acessível da tabela. */
  label: string;
  /** Nome acessível da região rolável que a envolve. */
  regionLabel: string;
  columns: ReadonlyArray<DataTableColumn<Row>>;
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  /** Nome acessível da linha, usado pelo clique de conveniência. */
  rowLabel?: (row: Row) => string;
  onOpenRow?: (row: Row) => void;
  isRowSelected?: (row: Row) => boolean;
  sort?: DataTableSort;
  onSort?: (key: string) => void;
  /** Coluna final de ações, fora da configuração de colunas. */
  actions?: (row: Row) => ReactNode;
  actionsWidth?: number;
  empty: ReactNode;
  footer?: ReactNode;
  className?: string;
}

const SORT_DIRECTION_LABEL = {
  asc: "crescente",
  desc: "decrescente",
} as const;

export function DataTable<Row>({
  label,
  regionLabel,
  columns,
  rows,
  rowKey,
  rowLabel,
  onOpenRow,
  isRowSelected,
  sort,
  onSort,
  actions,
  actionsWidth = 96,
  empty,
  footer,
  className,
}: DataTableProps<Row>) {
  const helper = useMemo(() => createColumnHelper<Row>(), []);

  const definitions = useMemo<Array<ColumnDef<Row, unknown>>>(
    () =>
      columns.map((column) =>
        helper.display({
          id: column.id,
          header: column.label,
          cell: (context) => column.cell(context.row.original),
        }),
      ) as Array<ColumnDef<Row, unknown>>,
    [columns, helper],
  );

  const table = useReactTable({
    data: rows as Row[],
    columns: definitions,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalWidth = columns.reduce((sum, column) => sum + column.width, 0) + (actions ? actionsWidth : 0);
  const columnCount = columns.length + (actions ? 1 : 0);

  return (
    <section className={className ? `cx-table-card ${className}` : "cx-table-card"}>
      <div className="cx-table-scroll" tabIndex={0} role="region" aria-label={regionLabel}>
        <table className="cx-table" aria-label={label} style={{ minWidth: `${totalWidth}px` }}>
          <colgroup>
            {columns.map((column) => (
              <col key={column.id} style={{ width: `${column.width}px` }} />
            ))}
            {actions && <col style={{ width: `${actionsWidth}px` }} />}
          </colgroup>
          <thead>
            <tr>
              {columns.map((column) => {
                const active = Boolean(column.sortKey) && sort?.key === column.sortKey;
                return (
                  <th
                    key={column.id}
                    scope="col"
                    data-numeric={column.numeric || undefined}
                    data-sorted={active || undefined}
                    aria-sort={active ? (sort?.direction === "asc" ? "ascending" : "descending") : "none"}
                  >
                    {column.sortKey && onSort ? (
                      <button
                        type="button"
                        className="cx-th-sort"
                        onClick={() => onSort(column.sortKey!)}
                        aria-label={
                          active
                            ? `${column.label}, ordem ${SORT_DIRECTION_LABEL[sort!.direction]}. Inverter a ordem.`
                            : `Ordenar por ${column.label}`
                        }
                      >
                        <span>{column.label}</span>
                        {active &&
                          (sort?.direction === "asc" ? (
                            <ChevronUp size={13} aria-hidden="true" />
                          ) : (
                            <ChevronDown size={13} aria-hidden="true" />
                          ))}
                      </button>
                    ) : (
                      <span>{column.label}</span>
                    )}
                    {column.headerExtra}
                  </th>
                );
              })}
              {actions && (
                <th scope="col">
                  <span className="cx-sr-only">Ações</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columnCount}>{empty}</td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={rowKey(row.original)}
                  data-selected={isRowSelected?.(row.original) || undefined}
                  onClick={(event) => {
                    if (!onOpenRow) return;
                    // Clique em botão, link ou campo dentro da linha é dele.
                    if ((event.target as HTMLElement).closest("button,a,input,select,textarea")) return;
                    onOpenRow(row.original);
                  }}
                  title={onOpenRow && rowLabel ? rowLabel(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <td key={cell.id} data-numeric={columns[index]?.numeric || undefined}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                  {actions && <td className="cx-row-actions-cell">{actions(row.original)}</td>}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {footer && <footer className="cx-table-foot">{footer}</footer>}
    </section>
  );
}
